import React from "react";
import { Slider } from "./ui/slider";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Layers, Search, Volume2, VolumeX } from "lucide-react";
import { USGS_FEEDS, REGION_PRESETS } from "../lib/api";

export default function Filters({
  filters, setFilters, layers, setLayers, feed, setFeed, region, setRegion, sound, setSound
}) {
  return (
    <div className="space-y-4 p-4" data-testid="filters-panel">
      <div>
        <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-slate-400">Data Feed</div>
        <Select value={feed} onValueChange={setFeed}>
          <SelectTrigger className="border-slate-800 bg-slate-900 text-slate-100" data-testid="feed-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="border-slate-800 bg-slate-900 text-slate-100">
            <SelectItem value="combined">BMKG + USGS (Combined)</SelectItem>
            <SelectItem value="bmkg">BMKG Indonesia</SelectItem>
            {USGS_FEEDS.map((f) => <SelectItem key={f.id} value={`usgs:${f.id}`}>USGS · {f.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div>
        <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-slate-400">Region</div>
        <Select value={region} onValueChange={setRegion}>
          <SelectTrigger className="border-slate-800 bg-slate-900 text-slate-100" data-testid="region-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="border-slate-800 bg-slate-900 text-slate-100">
            {Object.keys(REGION_PRESETS).map((k) => (
              <SelectItem key={k} value={k}>{k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <div className="mb-1 flex items-center justify-between">
          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Min Magnitudo</span>
          <span className="font-mono text-xs text-cyan-400">M {filters.minMag.toFixed(1)}+</span>
        </div>
        <Slider min={0} max={9} step={0.5} value={[filters.minMag]} onValueChange={(v) => setFilters({ ...filters, minMag: v[0] })} data-testid="filter-min-mag" />
      </div>

      <div>
        <div className="mb-1 flex items-center justify-between">
          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Max Kedalaman</span>
          <span className="font-mono text-xs text-cyan-400">{filters.maxDepth} km</span>
        </div>
        <Slider min={10} max={700} step={10} value={[filters.maxDepth]} onValueChange={(v) => setFilters({ ...filters, maxDepth: v[0] })} data-testid="filter-max-depth" />
      </div>

      <div>
        <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-slate-400">Cari Lokasi</div>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-500" />
          <Input
            placeholder="Sumatra, Java, Japan…"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className="border-slate-800 bg-slate-900 pl-8 text-sm text-slate-100 placeholder:text-slate-600"
            data-testid="filter-search"
          />
        </div>
      </div>

      <div>
        <div className="mb-2 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-400">
          <Layers className="h-3 w-3" /> Layer Geologis
        </div>
        <div className="space-y-2">
          {[
            { k: "plates", label: "Batas Lempeng Tektonik", color: "bg-sky-400" },
            { k: "faults", label: "Megathrust & Patahan", color: "bg-red-500" },
            { k: "volcanoes", label: "Gunung Api Aktif", color: "bg-amber-500" },
            { k: "wildfires", label: "Titik Panas Karhutla", color: "bg-orange-600" },
          ].map((l) => (
            <label key={l.k} className="flex cursor-pointer items-center justify-between rounded-md bg-slate-900/60 px-3 py-2 hover:bg-slate-900">
              <div className="flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${l.color}`}></span>
                <span className="text-xs text-slate-200">{l.label}</span>
              </div>
              <Switch checked={layers[l.k]} onCheckedChange={(v) => setLayers({ ...layers, [l.k]: v })} data-testid={`layer-toggle-${l.k}`} />
            </label>
          ))}
        </div>
        {layers.volcanoes && (
          <div className="mt-2 rounded-md border border-slate-800 bg-slate-900/40 p-2">
            <div className="mb-1.5 text-[9px] font-bold uppercase tracking-widest text-slate-400">Level Aktivitas Gunung</div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px]">
              <span className="flex items-center gap-1"><span className="inline-block h-0 w-0 border-l-[5px] border-r-[5px] border-b-[8px] border-l-transparent border-r-transparent" style={{ borderBottomColor: "#facc15" }}></span><span className="text-slate-300">Normal</span></span>
              <span className="flex items-center gap-1"><span className="inline-block h-0 w-0 border-l-[5px] border-r-[5px] border-b-[8px] border-l-transparent border-r-transparent" style={{ borderBottomColor: "#f59e0b" }}></span><span className="text-slate-300">Waspada</span></span>
              <span className="flex items-center gap-1"><span className="inline-block h-0 w-0 border-l-[5px] border-r-[5px] border-b-[8px] border-l-transparent border-r-transparent" style={{ borderBottomColor: "#f97316" }}></span><span className="text-slate-300">Siaga</span></span>
              <span className="flex items-center gap-1"><span className="inline-block h-0 w-0 border-l-[5px] border-r-[5px] border-b-[8px] border-l-transparent border-r-transparent" style={{ borderBottomColor: "#dc2626" }}></span><span className="text-slate-300">Awas</span></span>
            </div>
          </div>
        )}
        {layers.wildfires && (
          <div className="mt-2 rounded-md border border-slate-800 bg-slate-900/40 p-2 space-y-2">
            <div className="text-[9px] font-bold uppercase tracking-widest text-slate-400">Filter Titik Api (FIRMS + EONET)</div>
            <div className="flex gap-1">
              {[
                { id: "southeast_asia", label: "Indonesia / SEA" },
                { id: "global", label: "Global" },
              ].map((r) => (
                <button
                  key={r.id}
                  onClick={() => setFilters({ ...filters, fireRegion: r.id })}
                  className={`flex-1 rounded px-2 py-1 text-[10px] font-semibold ${filters.fireRegion === r.id ? "bg-orange-500 text-slate-950" : "bg-slate-900 text-slate-300 hover:bg-slate-800"}`}
                  data-testid={`fire-region-${r.id}`}
                >{r.label}</button>
              ))}
            </div>
            <div>
              <div className="mb-1 flex items-center justify-between">
                <span className="text-[9px] font-bold uppercase tracking-widest text-slate-400">Min FRP</span>
                <span className="font-mono text-[10px] text-orange-400">{filters.minFrp} MW</span>
              </div>
              <Slider min={0} max={100} step={5} value={[filters.minFrp]} onValueChange={(v) => setFilters({ ...filters, minFrp: v[0] })} data-testid="filter-min-frp" />
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px]">
              <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-yellow-400"></span><span className="text-slate-300">Ringan &lt;20 MW</span></span>
              <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-orange-500"></span><span className="text-slate-300">Sedang 20–50</span></span>
              <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-red-600"></span><span className="text-slate-300">Tinggi &gt;50 / High conf</span></span>
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between rounded-md bg-slate-900/60 px-3 py-2">
        <div className="flex items-center gap-2 text-xs text-slate-200">
          {sound ? <Volume2 className="h-3.5 w-3.5 text-cyan-400" /> : <VolumeX className="h-3.5 w-3.5 text-slate-500" />}
          Alarm suara M ≥ 5.0
        </div>
        <Switch checked={sound} onCheckedChange={setSound} data-testid="sound-toggle" />
      </div>
    </div>
  );
}
